"use client";

import { useState, useMemo, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { api } from "~/trpc/react";
import { ArelloLogo, ArelloBackground } from "~/components/icons";

function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export default function ForgotPasswordPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const isEmailValid = useMemo(() => isValidEmail(email), [email]);

  const requestReset = api.auth.requestPasswordReset.useMutation({
    onSuccess: () => {
      router.push(`/forgot-password/sent?email=${encodeURIComponent(email)}`);
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isEmailValid) return;
    
    setIsLoading(true);
    requestReset.mutate({ email });
  };

  return (
    <div className="flex min-h-screen items-start bg-white">
      {/* Left Side - Brand and Features */}
      <div className="hidden lg:flex lg:flex-1 relative h-screen overflow-auto">
        {/* Background */}
        <div className="absolute inset-0">
          <ArelloBackground className="absolute inset-0 w-full h-full" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full h-full px-8 py-12">
          <div className="max-w-[624px] w-full space-y-12">
            {/* Header Section */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-end gap-3">
                    <h1
                      className="text-[56px] leading-[68px] tracking-[-0.4px] text-white font-normal"
                      style={{ fontFamily: "Optima, serif" }}
                    >
                      Welcome to
                    </h1>
                    <div className="h-16 w-[235px] relative">
                      <ArelloLogo variant="light" className="w-full h-full" />
                    </div>
                  </div>
                  <p className="text-2xl italic text-[#fefdfc] tracking-[-0.1px] leading-[30px]">
                    Disciplinary Action Database
                  </p>
                </div>
              </div>

              <p className="text-base text-[rgba(255,255,255,0.8)] leading-6">
                A comprehensive web-based interface for real estate licensing authorities to share and access disciplinary action information.
              </p>
            </div>

            {/* Footer */}
            <div className="pt-8">
              <p className="text-xs text-[rgba(246,249,253,0.6)] leading-[18px]">
                © 2025 ARELLO. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Forgot Password Form */}
      <div className="w-full lg:w-[640px] h-screen flex items-center justify-center px-6 py-12 bg-[#fcfdfe]">
        <div className="w-full max-w-[420px] space-y-8">
          {/* Mobile Header - Only shown on small screens */}
          <div className="lg:hidden text-center space-y-4 mb-12">
            <div className="flex flex-col items-center gap-2">
              <h1
                className="text-4xl leading-tight tracking-[-0.4px] text-[#1c2024] font-normal"
                style={{ fontFamily: "Optima, serif" }}
              >
                Welcome to
              </h1>
              <div className="h-14 w-[180px] relative">
                <ArelloLogo variant="dark" className="w-full h-full" />
              </div>
            </div>
            <p className="text-lg italic text-[#1c2024] tracking-[-0.1px]">
              Disciplinary Action Database
            </p>
          </div>

          {/* Form Header */}
          <div className="space-y-2">
            <h2 className="text-[28px] font-semibold text-[#1c2024] leading-9 tracking-[-0.12px]">
              Reset Password
            </h2>
            <p className="text-sm text-[#60646c] leading-5">
            Enter the email associated with your account and we will send you an email with instructions to reset your password.
            </p>
          </div>

          {/* Forgot Password Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-[#1c2024] leading-5"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="w-full h-8 px-2 py-0 text-sm bg-[rgba(255,255,255,0.9)] border border-[rgba(0,9,50,0.12)] rounded text-[#1c2024] placeholder:text-[rgba(0,5,29,0.45)] focus:outline-none focus:ring-2 focus:ring-[#1c3144] focus:border-transparent"
              />
            </div>

            {/* Send Email Button */}
            <button
              type="submit"
              disabled={!isEmailValid || isLoading}
              className="w-full h-10 px-4 bg-[#1c3144] text-[#f6f9fd] text-base font-semibold rounded-md hover:bg-[#2a4558] focus:outline-none focus:ring-2 focus:ring-[#1c3144] focus:ring-offset-2 transition-colors disabled:bg-[rgba(0,0,51,0.06)] disabled:text-[rgba(0,8,48,0.27)] disabled:cursor-not-allowed"
            >
              {isLoading ? "Sending..." : "Send Email"}
            </button>
          </form>

          {/* Login Link */}
          <div className="pt-6 border-t border-[rgba(0,0,45,0.09)] text-center">
            <p className="text-sm text-[rgba(0,0,0,0.7)] leading-5">
              Remember your password?{" "}
              <Link
                href="/login"
                className="font-semibold text-[rgba(0,50,97,0.7)] hover:text-[rgba(0,50,97,0.9)] transition-colors"
              >
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

